import { Spin } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Header from "../../components/header";
import {
  fetchDashboardAllActivityCount,
  fetchDashboardContentInfo,
  fetchDashboardLeadCount,
  fetchDashboardSalesCount,
} from "../../services/dashboardService";
import DashboardHeader from "./DashboardHeader";
import DashboardTabs from "./DashboardTabs";
import { Tabs } from "./dashboard.types";
import { getDateRange } from "../../components/MomentFun";
import logger, { Logger } from '../../utils/logger';
import DashboardVisibilitySettings from "./DashboardVisibilitySettings";
import { useDashboardVisibility } from "./hooks/useDashboardVisibility";
import ReportTabsManager from "./ReportTabsManager";
import ReportSelectorPopup from "./ReportSelectorPopup";


interface TabItem {
  key: string;
  label: string;
  icon: string;
}

export interface ICount {
  leadAssignedCount: number;
  leadCreateCount: number;
  totalLead: number;
}
export interface DateRange {
  from: string;
  to: string;
}

const Dashboard: React.FC = () => {
  const renderCount = React.useRef(0);
  renderCount.current++;
  if (renderCount.current > 3) {
    console.warn(`Dashboard RENDER #${renderCount.current} - Too many re-renders!`);
  }

  // OPTIMIZED: Only select what Dashboard actually uses
  const userPreferences = useSelector((state: any) => state?.rootReducers?.user?.userPreferences);

  const [filterFromDate, setFilterFromDate] = useState(7);
  const [fromDate, setFromDate] = useState("7d");

  const dateManager = React.useMemo(() => {
    const mgr = require('./dashboardManager').DashboardDateManager.getInstance();
    return mgr;
  }, []);

  const initialRange = React.useMemo(() => dateManager.calculateDateRange('7d'), [dateManager]);

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    from: initialRange.from,
    to: initialRange.to,
  });
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [leadTotals, setLeadTotals] = useState<any>({
    leadAssignedCount: 0,
    leadCreateCount: 0,
    totalLead: 0,
  });
  const [totalActivityCount, setTotalActivityCount] = useState<number>(0);
  const [totalCallCount, setTotalCallCount] = useState<number>(0);
  const [totalSalesCount, setTotalSalesCount] = useState<number>(0);
  const [isCustomDate, setIsCustomDate] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const [isOrganization, setIsOrganization] = useState<boolean>(false);
  const [isPageLoad, setIsPageLoad] = useState<boolean>(false);
  const [visibilitySettingsOpen, setVisibilitySettingsOpen] = useState(false);
  const [reportSelectorOpen, setReportSelectorOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | undefined>();
  const [selectedReportTitle, setSelectedReportTitle] = useState<string | undefined>();

  // Initialize visibility configuration
  const {
    visibilityConfig,
    isLoading: visibilityLoading,
    saveConfig,
    shouldRenderComponent,
    getEnabledCount,
    getTotalCount,
  } = useDashboardVisibility();

  const [tabName, setTabName] = useState<Tabs>("lead");
  const [contentCountInfo, setContentCountInfo] = useState<any>({
    share: 0,
    view: 0,
  });

  // Simple cache to avoid refetching unchanged data
  const [dataCache, setDataCache] = useState<any>({});
  const [lastFetchKey, setLastFetchKey] = useState<string>('');

  // Calculate current fromDate and toDate based on filters
  const currentFromDate = isCustomDate
    ? customFromDate
    : selectedDateRange.from;
  const currentToDate = isCustomDate
    ? customToDate
    : selectedDateRange.to;

  // Simplified data loading - only for top cards + secondary metrics
  // Individual reports fetch their own data when opened
  const loadDataForCurrentTab = async (specificTab?: string) => {
    try {
      const currentTab = specificTab || tabName;

      // Create cache key based on current filters
      const cacheKey = `${currentTab}-${currentFromDate}-${currentToDate}-${selectedUserId}-${selectedTeamId}-${isOrganization}`;

      // Check if we have recent data for the same parameters
      if (lastFetchKey === cacheKey && Date.now() - (dataCache.timestamp || 0) < 30000) {
        Logger.log('Using cached dashboard data');
        return;
      }

      Logger.log('Loading dashboard data (top cards + secondary metrics)');

      const coreDataPromises = [];

      // Lead data (for Leads count + Contacted% + Conversion%)
      coreDataPromises.push(DashboardLeadCount());

      // Activity data (for Calls count + Activity count)
      coreDataPromises.push(DashboardAllActivityData());

      // Sales data (for Sales total)
      coreDataPromises.push(DashboardSalesData());

      // Content data (for Views + Shares) - only if secondary metrics visible
      if (shouldRenderComponent('metrics', 'secondaryMetrics')) {
        coreDataPromises.push(getContentCount());
      }

      // Load all core data in parallel
      await Promise.all(coreDataPromises);

      // Update cache
      setDataCache({ timestamp: Date.now() });
      setLastFetchKey(cacheKey);

      Logger.log('Dashboard cards data loaded successfully');
    } catch (error) {
      Logger.log('Error loading dashboard data:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (!isCustomDate && selectedDateRange.from && selectedDateRange.to && !visibilityLoading) {
      loadDataForCurrentTab();
    }
  }, [selectedDateRange, isCustomDate, visibilityLoading]);


  useEffect(() => {
    if (customFromDate && customToDate && !visibilityLoading) {
      loadDataForCurrentTab();
      setSelectedDateRange({ from: customFromDate, to: customToDate });
    }
  }, [customFromDate, customToDate, visibilityLoading]);

  useEffect(() => {
    if (selectedUserId && !visibilityLoading) {
      loadDataForCurrentTab();
    }
  }, [selectedUserId, visibilityLoading]);

  useEffect(() => {
    if (selectedTeamId && !visibilityLoading) {
      loadDataForCurrentTab();
    }
  }, [selectedTeamId, visibilityLoading]);

  useEffect(() => {
    if (isOrganization && !visibilityLoading) {
      loadDataForCurrentTab();
    }
  }, [isOrganization, visibilityLoading]);

  const getContentCount = async () => {
    try {
      let fromDate = isCustomDate
        ? customFromDate
        : selectedDateRange.from;
      let toDate = isCustomDate
        ? customToDate
        : selectedDateRange.to;
      const { data } = await fetchDashboardContentInfo(
        fromDate,
        toDate,
        isOrganization,
        selectedUserId,
        selectedTeamId
      );
      if (data?.data) {
        let share =
          data?.data?.contentTypeCount?.find((e: any) => e.type === "shared")
            ?.count || 0;

        let view =
          data?.data?.contentTypeCount?.find((e: any) => e.type === "view")
            ?.count || 0;

        setContentCountInfo({
          share,
          view,
        });
      }
    } catch (error) {}
  };

  const DashboardLeadCount = async () => {
    try {
      setIsPageLoad(true);
      let fromDate = isCustomDate
        ? customFromDate
        : selectedDateRange.from;
      let toDate = isCustomDate
        ? customToDate
        : selectedDateRange.to;

      const response = await fetchDashboardLeadCount(
        fromDate,
        toDate,
        isOrganization,
        selectedUserId,
        selectedTeamId,
        selectedListIds.length > 0 ? selectedListIds.join(',') : undefined
      );

      if (response && response.status) {
        setLeadTotals(response.data.data?.count);
        setIsPageLoad(false);
      }
    } catch (err) {
      console.error('API Error:', err);
      setIsPageLoad(false);
    }
  };

  const DashboardAllActivityData = async () => {
    try {
      setIsPageLoad(true);
      let fromDate = isCustomDate
        ? customFromDate
        : selectedDateRange.from;
      let toDate = isCustomDate
        ? customToDate
        : selectedDateRange.to;
      const response = await fetchDashboardAllActivityCount(
        fromDate,
        toDate,
        isOrganization,
        selectedUserId,
        selectedTeamId,
        selectedListIds.length > 0 ? selectedListIds.join(',') : undefined
      );
      if (response && response.status) {
        let resposneData = response.data.data;
        let tempCallingData = resposneData?.callingCount;
        let callCount = 0;
        tempCallingData.forEach((item: any) => {
          callCount += item.count;
        });
        setTotalCallCount(callCount);
        setTotalActivityCount(resposneData?.totalActivity);
        setIsPageLoad(false);
      }
    } catch (err) {
      Logger.log(err);
      setIsPageLoad(false);
    }
  };

  const DashboardSalesData = async () => {
    try {
      setIsPageLoad(true);
      let fromDate = isCustomDate
        ? customFromDate
        : selectedDateRange.from;
      let toDate = isCustomDate
        ? customToDate
        : selectedDateRange.to;
      const response = await fetchDashboardSalesCount(
        fromDate,
        toDate,
        isOrganization,
        selectedUserId,
        selectedTeamId,
        selectedListIds.length > 0 ? selectedListIds.join(',') : undefined
      );
      if (response && response.status) {
        let resposneData = response.data.data;
        setTotalSalesCount(resposneData?.salesValue || 0);
        setIsPageLoad(false);
      }
    } catch (err) {
      Logger.log(err);
      setIsPageLoad(false);
    }
  };

  const handleDashboardRefresh = async () => {
    logger.log('Dashboard refresh triggered');

    try {
      // Clear cache to force fresh data
      setDataCache({});
      setLastFetchKey('');

      await loadDataForCurrentTab();
      logger.log('Dashboard refresh completed successfully');
    } catch (error) {
      console.error('Dashboard refresh failed:', error);
      throw error;
    }
  };

  const handleReportSelect = (reportId: string, reportTitle: string) => {
    setReportSelectorOpen(false);
    setSelectedReportId(reportId);
    setSelectedReportTitle(reportTitle);
  };

  const handleApplyFilters = (filters: any) => {
    Logger.log('Applying batched filters:', filters);

    const isCustom = filters.dateRange === 'custom';
    setFromDate(filters.dateRange);
    setIsCustomDate(isCustom);

    if (isCustom && filters.customFromDate && filters.customToDate) {
      setCustomFromDate(filters.customFromDate);
      setCustomToDate(filters.customToDate);
      setSelectedDateRange({ from: filters.customFromDate, to: filters.customToDate });
    } else {
      setCustomFromDate('');
      setCustomToDate('');
      const range = getDateRange(filters.dateRange);
      setSelectedDateRange(range);
    }

    if (filters.teamOrOrg === 'organization') {
      setIsOrganization(true);
      setSelectedTeamId('');
      setSelectedUserId('');
    } else if (filters.teamOrOrg) {
      setSelectedTeamId(filters.teamOrOrg);
      setIsOrganization(false);
      setSelectedUserId(filters.teamMember || '');
    } else {
      setSelectedTeamId('');
      setIsOrganization(false);
      setSelectedUserId(filters.teamMember || '');
    }

    setSelectedListIds(filters.listIds || []);
  };

  const handleClearFilters = () => {
    setFromDate('7d');
    setIsCustomDate(false);
    setCustomFromDate('');
    setCustomToDate('');
    setSelectedTeamId('');
    setSelectedUserId('');
    setIsOrganization(false);
    setSelectedListIds([]);
  };

  const handleTabOnchange = (tab: Tabs) => {
    setTabName(tab);

    if (tab === "reports") {
      setReportSelectorOpen(true);
      return;
    }
  };

  const handleFilteronChange = (date: string) => {
    if (date === 'custom') {
      setFromDate(date);
      setIsCustomDate(true);
      return;
    }

    const range = getDateRange(date);

    setSelectedDateRange(range);
    setFromDate(date);
    setIsCustomDate(false);

    const daysDiff = moment(range.to).diff(moment(range.from), 'days');
    setFilterFromDate(daysDiff);
  };

  const selectFilterDate = (e: any) => {
    const { name, value } = e.currentTarget;

    if (name === "from") {
      const utcDate = moment(value).utc().startOf('day').format();
      setCustomFromDate(utcDate);
      setFromDate('custom');
      setIsCustomDate(true);
    } else {
      const utcDate = moment(value).utc().endOf('day').format();
      setCustomToDate(utcDate);
      setFromDate('custom');
      setIsCustomDate(true);
    }
  };

  const selectFilterUser = (value: string, tab: string) => {
    if (!value) {
      if (tab === "team" || tab === "organization") {
        setSelectedTeamId("");
        setIsOrganization(false);
        setSelectedUserId("");
      } else if (tab === "Emp") {
        setSelectedUserId("");
      }
      return;
    }

    if (tab === "Emp") {
      setSelectedUserId(value);
    } else if (value === "organization") {
      setIsOrganization(true);
      setSelectedTeamId("");
      setSelectedUserId("");
    } else {
      setSelectedTeamId(value);
      setIsOrganization(false);
      setSelectedUserId("");
    }
  };

  const secondaryTabsArray: TabItem[] = [
    {
      key: "contacted",
      label: "Leads contacted",
      icon: "fas fa-user-check"
    },
    {
      key: "conversion",
      label: "Lead conversion ratio",
      icon: "fas fa-exchange-alt"
    },
    {
      key: "views",
      label: "Content views",
      icon: "fas fa-eye"
    },
    {
      key: "shares",
      label: "Content shares",
      icon: "fas fa-share-alt"
    }
  ];


  return (
    <div id="main" className="main">
      <Header />
      <section style={{ paddingLeft: 10, paddingTop: 2 }}>
       <DashboardHeader
  fromDate={fromDate}
  handleFilteronChange={handleFilteronChange}
  selectFilterUser={selectFilterUser}
  selectFilterDate={selectFilterDate}
  isCustomDate={isCustomDate}
  customFromDate={customFromDate}
  customToDate={customToDate}
  toDate={currentToDate}
  handleRefresh={handleDashboardRefresh}
  onToggleVisibilitySettings={() => setVisibilitySettingsOpen(true)}
  selectedTeamId={selectedTeamId}
  selectedUserId={selectedUserId}
  isOrganization={isOrganization}
  selectedListIds={selectedListIds}
  onApplyFilters={handleApplyFilters}
  onClearFilters={handleClearFilters}
/>
        <Spin tip="Loading..." spinning={isPageLoad}>
       <DashboardTabs
  handleTabOnchange={handleTabOnchange}
  selectedTab={tabName}
  totalLeadCount={leadTotals.totalLead}
  totalCallCount={totalCallCount}
  totalActivityCount={totalActivityCount}
  totalSalesCount={totalSalesCount}
  onOpenCallLogs={() => {}}
/>

{shouldRenderComponent('metrics', 'secondaryMetrics') && (
<div className="dashboard-section-1">
  <div className="row">
    <ul className="nav nav-pills ml-0 justify-content-start d-flex dashboard_cards" style={{ gap: "8px", marginBottom: "16px", marginTop: "4px" }}>
      {secondaryTabsArray.map((tab: TabItem) => (
        <li
          key={tab.key}
          className="nav-item bg-transparent"
          role="presentation"
          style={{ flex: 1 }}
        >
          <button
            className="nav-link dashboard-section-1-sub-1 bg-white w-100"
            style={{
              background: "white",
              padding: "6px 12px",
              borderRadius: "4px !important",
              boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
              border: "none",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
            >
            <div className="d-flex align-items-center gap-2">
              <i className={tab.icon} style={{ color: "#64748b", fontSize: "14px" }} />
              <span style={{ color: "#64748b", fontSize: "14px", whiteSpace: "nowrap" }}>
                {tab.label}
              </span>
            </div>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#0f172a" }}>
              {tab.key === "views" && contentCountInfo?.view}
              {tab.key === "shares" && contentCountInfo?.share}
              {tab.key === "contacted" && `${leadTotals?.leadContactedRatio ? Math.round(leadTotals?.leadContactedRatio) : 0}%`}
              {tab.key === "conversion" && `${leadTotals?.leadConversionRatio ? Math.round(leadTotals?.leadConversionRatio) : 0}%`}
            </span>
          </button>
        </li>
      ))}
    </ul>
  </div>
</div>
)}

          <ReportSelectorPopup
            isOpen={reportSelectorOpen}
            onClose={() => setReportSelectorOpen(false)}
            onReportSelect={handleReportSelect}
          />

          <ReportTabsManager
            onClose={() => {}}
            fromDate={selectedDateRange.from}
            toDate={selectedDateRange.to}
            selectedUserId={selectedUserId}
            selectedTeamId={selectedTeamId}
            isOrganization={isOrganization}
            initialReportId={selectedReportId}
            initialReportTitle={selectedReportTitle}
            visibilityConfig={visibilityConfig}
          />
        </Spin>
      </section>

      <DashboardVisibilitySettings
        open={visibilitySettingsOpen}
        onClose={() => setVisibilitySettingsOpen(false)}
        config={visibilityConfig}
        onConfigChange={(config) => saveConfig(config)}
      />

    </div>
  );
};

export default Dashboard;
